import { createAction, createReducer } from 'deox';
import { pick, without, lensPath, set } from 'ramda';
import { resetInstantCredit } from './resetInstantCredit';

// IMPORTANT!
// Must update the instantCreditKeys array if this interface is updated.
//
// There's no good way to convert the interface keys into an array at the
// moment, and there's no way to enforce that the array contains all elements
// of a union, so TS cannot check that the array is exhaustive.
export interface InstantCredit extends CreditAccount {
  returnCode?: string;
  errorCode?: string;
  hasExistingAccount?: boolean;
  apr?: string;
  bureauCode?: string;
  bureauScore?: string;
  isApproved?: boolean;
  isDeclined?: boolean;
  message?: string;
  notBestAprContent?: string;
  rejectReasons?: string;
  tenantCode?: string;
}

export interface CreditAccount {
  accountId?: string;
  accountNumber?: string;
  applicationId?: string;
  authAllowed?: number;
  callId?: string;
  cardArtImageId?: string;
  cardType?: string;
  creditLimit?: number;
  creditTermNumber?: string;
  expirationDate?: string;
  expirationType?: string;
  expirationValue?: number;
  firstName?: string;
  lastName?: string;
  firstPurchaseLimit?: number;
  logo?: string;
  middleInitial?: string;
  openToBuy?: number;
  rewards?: Rewards;
  securityCode?: string;
  virtualCardAccountNumber?: string;
  wallets?: Wallets;
  divisionNumber?: number;
}

export interface Rewards {
  currentLevelName: string;
  currentLevelNumber: string | number;
  certificates: Certificate[];
}

export interface Certificate {
  certificateNumber: string;
  fulfillDate: string;
  expireDate: string; // 'yyyy-mm-dd'
  rewardAmount: number;
  barcodeIndicator?: '128' | '3of9';
  pinCode?: string;
}

export interface Wallets {
  eligibleOnFileWallets: Wallet[];
  onDeviceWallets: Wallet[];
}

export interface Wallet {
  assetId: string;
  assetData: string;
  assetType: string;
  consumerFacingEntityName: string;
  walletType: 'MERCHANT' | 'WALLET';
  walletId: string;
}

const instantCreditKeys: (keyof InstantCredit)[] = [
  'returnCode',
  'errorCode',
  'firstName',
  'middleInitial',
  'lastName',
  'accountNumber',
  'callId',
  'virtualCardAccountNumber',
  'expirationDate',
  'expirationType',
  'expirationValue',
  'securityCode',
  'firstPurchaseLimit',
  'authAllowed',
  'apr',
  'rejectReasons',
  'creditLimit',
  'bureauCode',
  'bureauScore',
  'openToBuy',
  'cardType',
  'logo',
  'notBestAprContent',
  'message',
  'hasExistingAccount',
  'isApproved',
  'isDeclined',
  'rewards',
  'wallets',
  'applicationId',
  'accountId',
  'cardArtImageId',
  'divisionNumber',
  'tenantCode',
];

export interface InstantCreditState {
  currentEntity: InstantCredit;
  entities: InstantCredit[];
  meta: {
    timedOut: boolean;
  };
}

export const defaultState: InstantCreditState = {
  currentEntity: {},
  entities: [],
  meta: {
    /**
     * Instant credit is considered timed out by default (inaccessible)
     * until request hooks sets data on it (accessible)
     * When it timed out, we reset the IC, turning it back to be inaccessible.
     */
    timedOut: true,
  },
};

export const mergeInstantCredit = createAction(
  'MERGE_INSTANT_CREDIT',
  resolve => (data: Partial<InstantCredit>) => resolve(data),
);

export const setCurrentAccount = createAction(
  'SET_CURRENT_ACCOUNT',
  resolve => (data: Partial<InstantCredit>) => resolve(data),
);

export const setAccounts = createAction(
  'SET_ACCOUNTS',
  resolve => (data: InstantCredit[]) => resolve(data),
);

export const removeWallet = createAction(
  'REMOVE_WALLET',
  resolve => (data: Wallet) => resolve(data),
);

export const instantCreditReducer = createReducer(
  defaultState,
  handleAction => [
    handleAction(mergeInstantCredit, (state, { payload: data }) => {
      return {
        ...state,
        currentEntity: {
          ...state.currentEntity,
          ...pick(instantCreditKeys, data),
        },
        meta: {
          timedOut: false,
        },
      };
    }),

    handleAction(setCurrentAccount, (state, { payload: data }) => {
      return {
        ...state,
        currentEntity: {
          ...defaultState.currentEntity,
          ...pick(instantCreditKeys, data),
        },
      };
    }),

    handleAction(setAccounts, (state, { payload: data }) => {
      return {
        ...state,
        entities: data,
        meta: {
          timedOut: false,
        },
      };
    }),

    handleAction(resetInstantCredit, () => defaultState),

    handleAction(removeWallet, (state, action) => {
      if (!state.currentEntity.wallets) {
        return state;
      }
      const removeSelected = without([action.payload]);

      const wallets = state.currentEntity.wallets;
      const updatedWallet: Wallets = {
        eligibleOnFileWallets: removeSelected(wallets.eligibleOnFileWallets),
        onDeviceWallets: removeSelected(wallets.onDeviceWallets),
      };
      return set(lensPath(['currentEntity', 'wallets']), updatedWallet, state);
    }),
  ],
);

export interface MountedState {
  instantCredit: ReturnType<typeof instantCreditReducer>;
}

export const selectInstantCredit = (state: MountedState): InstantCredit =>
  state.instantCredit.currentEntity;

export const selectAccounts = (state: MountedState): InstantCredit[] =>
  state.instantCredit.entities;

export const selectInstantCreditTimedOut = (state: MountedState): boolean =>
  state.instantCredit.meta.timedOut;

export default instantCreditReducer;
